"use strict";
import React from "react";
import { Link } from "react-router-dom";
import FormsyFields from "./formsyFields";
import Formsy from "./formsy";
import FormsyInputInteractive from "./prism/FormsyElements/FormsyInputInteractive"
import loaders from "./loaders";
import PrismRow from "./prism/utilities/prismRow";
import PrismButton from "./prism/button/prismButton";
import PrismLink from "./prism/link/prismLink";
import PrismSpinner from "./prism/spinner/prismSpinner";
import PrismMessage from "./prism/message/prismMessage";
import {
    getPasswordValidation
} from '../util/validationUtils';
import {
    redirectToDefaultPage
} from '../util/helpers';

const initComponent = (flex = window.flex) => {
    class ResetPassword extends React.PureComponent {
        state = {
            accountSettings: [],
            canSubmit: false,
            dataReady: false,
            passwordValue: "",
            currentPasswordValue: "",
            alertMsg: "",
            messageTitle: "",
            messageType: "",
            accountLocked: false,
            emailSent: false,
            isLoading: false,
            tryCounter: 0,
            isSystemMessage: false,
            isPasswordExpired: false
        };

        enableButton = () => {
            this.setState({
                canSubmit: true
            });
        };

        disableButton = () => {
            this.setState({
                canSubmit: false
            });
        };

        componentDidMount() {
            const { userId, passwordResetToken, errorCode } = this.props;

            // check if there is a passwordResetToken & userId
            if (passwordResetToken && userId) {
                this.validateToken(passwordResetToken, userId);
            }
            else {
                redirectToDefaultPage();
            }

            if (errorCode === "750") { this.setPasswordExpired(); }
        }

        validateToken = (passwordResetToken, userId) => {
            const self = this;
            self.setState({ canSubmit: false });

            $.ajax({
                url: flex.getApiUrl("/api/v1/auth/validatetoken"),
                async: true,
                type: "POST",
                dataType: "json",
                contentType: "application/json",
                data: JSON.stringify({ passwordResetToken: passwordResetToken, userId: userId }),
                headers: flex.getHeaders(),
                success: function (data) {
                    flex.setBusy("app:validateToken", false);
                    if (!this.unmounting) {
                        self.setState(
                            {
                                accountSettings: data || null,
                                canSubmit: true,
                                dataReady: true
                            });
                    }
                }.bind(this),
                error: function (jqXHR, textStatus, errorThrown) {
                    flex.handleAjaxError(jqXHR, textStatus, errorThrown);
                    self.setState({ accountSettings: null, alertMsg: jqXHR.responseJSON.msg, canSubmit: false, dataReady: true });
                    flex.addPageAlerts({
                        className: 'alert-success',
                        summary: jqXHR.responseJSON.msg,
                        selfCloseTimeout: 15000
                    });
                    setTimeout(function () {
                        redirectToDefaultPage();
                    }, 5000);
                }
            });
        };

        handleSubmit = (model, reset, invalidate) => {
            const self = this;
            const { tryCounter } = self.state;
            
            self.setState({ canSubmit: false, isLoading: true });

            const { userId, passwordResetToken } = this.props;

            if (passwordResetToken && userId) {
                this.validateToken(passwordResetToken, userId);
            }

            $.ajax({
                url: flex.getApiUrl("/api/v1/auth/resetpassword"),
                async: true,
                type: "POST",
                dataType: "json",
                data: JSON.stringify(model),
                contentType: "application/json",
                headers: flex.getHeaders(),
                success: function (data) {
                    if (!self.unmounting) {
                        self.setState({ 
                            emailSent: data.emailSent,
                            alertMsg: "Your password has been reset. Please use your new password to sign in again.",
                            dataReady: true,
                            messageTitle: "",
                            messageType: "info",
                            isLoading: false,
                            isSystemMessage: true,
                            isPasswordExpired: false
                        });
                        setTimeout(function () {
                            redirectToDefaultPage();
                        }, 5000);
                    }
                }.bind(this),
                error: function (jqXHR, textStatus, errorThrown) {
                    let counter;
                    let failMessage = "";
                    let errors = jqXHR?.responseJSON?.errors;

                    if ((!errors || errors?.find(x => x.code === "500")) && jqXHR.status !== 400) {
                        counter = tryCounter + 1;
                        let clientCare = flex.text("UX_CONTACT_CLIENT_CARE");
                        self.setState({ tryCounter: counter, isSystemMessage: true });
                        failMessage = counter < 2 ? "Please try again. " + clientCare : clientCare;
                    } else {
                        flex.handleAjaxError(jqXHR, textStatus, errorThrown);

                        for (var i = 0; i < errors.length; i++) {
                            let minutes = 0;
                            let errorSummary = errors[i].summary;

                            // Jank to extract the number of minutes and replace with a humanized version.
                            if (errors[i].code === "GLOBAL_ERROR_REQUIREDFIELDS") {
                                errorSummary = flex.text("GLOBAL_ERROR_REQUIREDFIELDS");
                            }
                            else if (errors[i].summary?.indexOf("minutes") > -1) {
                                minutes = errors[i].summary.match(/\d+/g)[0];
                                var duration = moment.duration({ minutes: minutes });
                                errorSummary = errors[i].summary.replace("" + minutes + " minutes", duration.humanize());
                            }
                            
                            failMessage = errorSummary
                        }
                    }
                    self.setState({
                        alertMsg: failMessage,
                        canSubmit: true,
                        dataReady: true,
                        passwordValue: model.password,
                        currentPasswordValue: model.currentPassword,
                        messageTitle: "Something Went Wrong",
                        messageType: "danger",
                        isLoading: false
                    });
                    if (counter > 1) { setTimeout(function () { redirectToDefaultPage(); }, 5000); }
                }
            });
        };

        tryAgain = () => {
            this.setState({
                messageType: "info",
                alertMsg: "Please confirm and try again",
                isSystemMessage: false
            });
        };

        setPasswordExpired = () => {
            this.setState({
                isPasswordExpired: true,
                messageType: "info",
                alertMsg: "Your password is expired. Please create a new password to continue."
            });
        }

        render() {
            const {
                alertMsg,
                accountSettings,
                passwordValue,
                currentPasswordValue,
                isLoading,
                canSubmit,
                messageTitle,
                messageType,
                tryCounter,
                isSystemMessage,
                isPasswordExpired
            } = this.state;

            const { userId, passwordResetToken } = this.props;
            
            if (!this.state.dataReady) { return loaders.getLoader(); }

            const passwordValidation = getPasswordValidation();

            const accountResults = <div className="reset-password__container">
                    <div className="col-12">
                        <PrismRow>
                            <h1 className="reset-password__header">{flex.text("UX_LOGIN_HDR_RESETPASSWORD")}</h1>
                        </PrismRow>
                        {
                            isSystemMessage && alertMsg && alertMsg !== ''
                            ? <div>
                                <PrismRow>
                                    <PrismMessage title={messageTitle} color={messageType}>
                                        {alertMsg}
                                    </PrismMessage>
                                </PrismRow>
                                {
                                    messageType === "danger" && tryCounter < 2
                                    && <PrismRow>
                                        <div className="reset-password__retry-button">
                                            <PrismButton onClick={this.tryAgain} >
                                                Try Again
                                            </PrismButton>
                                        </div>
                                    </PrismRow>
                                }
                            </div>
                            : <div>
                                {
                                    messageType !== "success" && alertMsg
                                    && <PrismRow>
                                        <PrismMessage color={messageType}>{alertMsg}</PrismMessage>
                                    </PrismRow>
                                }
                                <PrismRow>
                                        <ul className="reset-password__rule">
                                            <li>Must be at least 8 characters</li>
                                            <li>Cannot be your Username</li>
                                            <li>Must include UPPER and lower case, at least 1 number and 1 special character</li>
                                        </ul>
                                </PrismRow>
                                <PrismRow>
                                    {
                                        isPasswordExpired &&
                                        <FormsyInputInteractive
                                            {...formProps}
                                            id="currentPassword"
                                            name="currentPassword"
                                            placeholder="Type current password"
                                            value={currentPasswordValue}
                                            type="password"
                                            required={true}
                                            prismStyle={true}
                                            autoComplete="off"
                                            disabled={isLoading}
                                            validationError="Current password is required"
                                            isFocus={true}
                                            iconLeft="prism-icon-locked icon"
                                        />
                                    }
                                    <FormsyInputInteractive
                                        {...formProps}
                                        id="password"
                                        name="password"
                                        placeholder="Type a new password"
                                        value={passwordValue}
                                        type="password"
                                        required={true}
                                        prismStyle={true}
                                        autoComplete="off"
                                        disabled={isLoading}
                                        validations={{
                                            ...passwordValidation.validators,
                                            notEqualToUserName: model => {
                                                const username = accountSettings && accountSettings.username;
                                                const password = model['password'];

                                                return !password || !username || password.toLowerCase() !== username.toLowerCase();
                                            }
                                        }}
                                        validationError="Password is required"
                                        validationErrors={{
                                            ...passwordValidation.errorMessages,
                                            notEqualToUserName: 'Password cannot be the same as Username.'
                                        }}
                                        isFocus={!isPasswordExpired}
                                        iconLeft="prism-icon-locked icon"
                                    />
                                    <FormsyInputInteractive
                                        {...formProps}
                                        id="password2"
                                        name="password2"
                                        placeholder="Re-enter new password"
                                        value={passwordValue}
                                        type="password"
                                        required={true}
                                        prismStyle={true}
                                        disabled={isLoading}
                                        autoComplete="off"
                                        validations="equalsField:password"
                                        validationError="Passwords do not match"
                                        iconLeft="prism-icon-locked icon"
                                    />
                                </PrismRow>
                                <PrismRow>
                                    <div className="reset-password__buttons-group">
                                        <span className="cancel-button">
                                            <PrismLink onClick={() => redirectToDefaultPage()}>{flex.text("UX_ACT_CANCEL")}</PrismLink>
                                        </span>
                                        <span>
                                            <PrismButton type="submit" disabled={!canSubmit}>
                                                {flex.text("UX_ACT_SUBMIT")}
                                                { isLoading && <span className="reset-password__spinner">
                                                    <PrismSpinner size='md'/>
                                                </span> }
                                            </PrismButton>
                                        </span>
                                    </div>
                                </PrismRow>
                                <FormsyFields.Input {...formProps} id="userId" name="userId" value={userId} type="hidden" autoComplete="off" />
                                <FormsyFields.Input {...formProps} id="passwordResetToken" name="passwordResetToken" value={passwordResetToken} type="hidden" autoComplete="off" />
                            </div>
                        }
                    </div>
                </div>;

            const results = accountSettings ? <div>{accountResults}</div> : false;

            const formProps = { layout: "vertical" };

            return (
                <div>
                    <Formsy.Form style={{ textAlign: "left" }} onValidSubmit={this.handleSubmit} onValid={this.enableButton} onInvalid={this.disableButton}>
                        <fieldset>
                            {results}
                        </fieldset>
                    </Formsy.Form>
                </div>
            );
        }
    }

    return ResetPassword;
};

module.exports = initComponent;
